/**
 * `/checkout` and `/order` routes. Drives the pay-now flow:
 *
 *   Supports both standard and SmartHQ URL formats:
 *   - Standard: /checkout?tokenId=...&country=NZ&type=onetime[&saveCard=1]
 *   - SmartHQ Phase A: /checkout?tokenId=...&country=NZ (fresh card entry)
 *   - SmartHQ Phase B: /order?tokenId=...&country=NZ[&storedPaymentMethodId=...]
 *
 *   Flow:
 *   → parse URL params (tokenId, country, type, saveCard, storedPaymentMethodId)
 *   → on `/order`, fetch the customer's saved card via
 *     GET /v1/payment-methods?view=smarthq, then mint the Adyen session against
 *     the resolved stored token and render <SavedCardForm/> — a one-click
 *     confirmation with no CVC prompt (the card is charged server-side)
 *   → on `/checkout`, dispatch createPaymentSession up-front and render
 *     <PaymentForm/> (fresh card entry)
 *   → Pay on `/order` → BFF headless confirm charges the vaulted card
 *     server-side (no CVC); if the issuer demands 3DS, <SavedCardForm/>
 *     submits the mounted one-click card for the Adyen challenge
 *   → on Adyen onPaymentCompleted → dispatch confirmOrder (showing a
 *     "Creating your order" overlay) → navigate to the status page
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageShell from '../../components/common/PageShell';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { parseCheckoutParams } from '../../lib/urlParams';
import { BffError, checkoutApi } from '../../services';
import { useQuery } from '../../utils/hooks';
import { loadSavedCards } from '../savedCards/savedCardsSlice';

import { clearError, createPaymentSession, resetCheckout } from './checkoutSlice';

/** Full-cover "Creating your order" overlay shown while BFF `/confirm` runs. */
const OrderProcessingOverlay: React.FC = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      background: 'rgba(0,0,0,0.7)',
      zIndex: 1100,
    }}
  >
    <Typography variant="h6" sx={{ color: '#fff' }}>
      Creating your order…
    </Typography>
    <LoadingSpinner isLoading size="large" />
  </Box>
);

const CheckoutPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const params = parseCheckoutParams(query);
  const { session, loading, error } = useAppSelector((state) => state.checkout);
  const savedCardsLoading = useAppSelector((state) => state.savedCards.loading);
  // SmartHQ Phase B `/order` resolves the customer's single saved card.
  const savedCard = useAppSelector((state) => state.savedCards.items[0]);
  const [processing, setProcessing] = useState(false);

  // SmartHQ Phase B: `/order` charges the customer's single saved card.
  const isOrderRoute = location.pathname === '/order';

  // Adyen captures `onPaymentCompleted` once at mount time, but the session can
  // change afterwards (it is minted asynchronously, and re-minted on a 3DS
  // retry). Read the session from a ref so confirm always sees the latest value.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // A single pay can surface a terminal outcome from several sources — the
  // headless confirm (`onPay`), the 3DS result (`onPaymentCompleted`), or a
  // transient Adyen `onError`. Guard so exactly ONE of them navigates, and so a
  // real payment result is never pre-empted by a transient SDK error (which
  // would otherwise flash `/status/error` before the true pending/success
  // redirect).
  const settledRef = useRef(false);

  // Guards the one-time payment-session mint against React StrictMode's dev
  // double-invoke so createPaymentSession fires EXACTLY once per set of params.
  const initKeyRef = useRef<string | null>(null);

  /** Navigate to a terminal status page at most once. */
  const finish = (path: string): void => {
    if (settledRef.current) return;
    settledRef.current = true;
    navigate(path);
  };

  useEffect(() => {
    if (!params) return;

    // Mint the payment session ONCE per params. In dev, StrictMode invokes this
    // effect twice (mount → cleanup → mount); the key guard skips the second
    // run so createPaymentSession is not called again. The cleanup below still
    // runs on every pass, so a real unmount resets the slice as before.
    const initKey = `${params.tokenId}|${params.country}|${String(params.saveCard)}|${String(
      params.storedPaymentMethodId,
    )}|${isOrderRoute}`;
    if (initKeyRef.current !== initKey) {
      initKeyRef.current = initKey;

      if (isOrderRoute) {
        // `/order` fetches the customer's saved card FIRST (payment-methods), then
        // mints the Adyen session against the resolved stored token so
        // <SavedCardForm> can mount the invisible one-click card up-front (for the
        // 3DS fallback). The card is required even when the URL carries a
        // storedPaymentMethodId, because the headless confirm charges by
        // `savedCard.id` and the session is minted against the stored token.
        void (async () => {
          try {
            const list = await dispatch(
              loadSavedCards({ tokenId: params.tokenId, view: 'smarthq' }),
            ).unwrap();
            const card = list.paymentMethods[0];
            if (!card) return; // the render shows the "no saved card" message
            const storedToken =
              card.adyen_stored_payment_method_id ?? card.adyen_recurring_detail_reference ?? '';
            await dispatch(
              createPaymentSession({
                tokenId: params.tokenId,
                countryCode: params.country,
                body: { storedPaymentMethodId: storedToken },
              }),
            );
          } catch {
            // loadSavedCards / createPaymentSession failures surface via the slice
            // error state (handled by the error branch below).
          }
        })();
      } else {
        // `/checkout` collects a fresh card, so the session is needed up-front.
        void dispatch(
          createPaymentSession({
            tokenId: params.tokenId,
            countryCode: params.country,
            body: params.storedPaymentMethodId
              ? { storedPaymentMethodId: params.storedPaymentMethodId }
              : { saveCard: params.saveCard },
          }),
        );
      }
    }

    return () => {
      dispatch(resetCheckout());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.tokenId, params?.country, params?.saveCard, params?.storedPaymentMethodId, isOrderRoute]);

  // Ref so the auto-trigger effect below can call the latest onPay without
  // capturing a stale closure (onPay is defined after the early returns but the
  // effect must be declared here, before them, to satisfy Rules of Hooks).
  const onPayRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Trigger the headless confirm automatically once the payment session and
  // saved card are both ready. No Adyen component is mounted for /order.
  const orderTriggeredRef = useRef(false);
  useEffect(() => {
    if (!isOrderRoute || !session || !savedCard) return;
    if (orderTriggeredRef.current) return;
    orderTriggeredRef.current = true;
    setProcessing(true);
    void onPayRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrderRoute, session?.adyenSessionId, savedCard?.id]);

  if (!params) {
    return (
      <PageShell>
        <div role="alert">Invalid checkout link — missing required parameters.</div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div role="alert">
          {error.code}: {error.message}{' '}
          <button type="button" onClick={() => dispatch(clearError())}>
            Dismiss
          </button>
        </div>
      </PageShell>
    );
  }

  // Headless confirm for `/order`. onPayRef is kept current each render so
  // the auto-trigger effect (declared before the early returns) always uses
  // the latest closure with fresh params/savedCard.
  const onPay = async (): Promise<void> => {
    const currentSession = sessionRef.current;
    if (!savedCard || !currentSession) {
      finish('/status/error');
      return;
    }
    try {
      const order = await checkoutApi.confirm({
        tokenId: params.tokenId,
        countryCode: params.country,
        checkoutToken: currentSession.checkoutSessionToken,
        body: {
          paymentMethodId: savedCard.id,
          ...(params.encryptedSecurityCode
            ? { encryptedSecurityCode: params.encryptedSecurityCode }
            : {}),
        },
      });
      // Carry the human-readable order reference (e.g. `SNZ8100000003`) through
      // to the status page as `?orderRef=…` so it stays visible in the URL.
      const orderRefQuery = order.orderRef
        ? `?${new URLSearchParams({ orderRef: order.orderRef }).toString()}`
        : '';
      finish(
        order.status === 'confirmed'
          ? `/status/success${orderRefQuery}`
          : `/status/pending${orderRefQuery}`,
      );
    } catch (err) {
      if (err instanceof BffError && err.code === 'payment_pending') {
        finish('/status/pending');
        return;
      }
      if (err instanceof BffError && err.code === 'payment_refused') {
        finish('/status/failed');
        return;
      }
      finish('/status/error');
    }
  };
  onPayRef.current = onPay;

  return (
    <PageShell loading={!processing && (isOrderRoute ? savedCardsLoading || loading : loading)}>
      {processing && <OrderProcessingOverlay />}
    </PageShell>
  );
};

export default CheckoutPage;
